import { useQuery } from '@tanstack/react-query';

import { conversationsUtilsEndpoints } from '@/api/endpoints';
import { callPost } from '@/api/fetcher';
import { LogMessage } from '@/shared/types/analytics';
import { isUuid } from '@/util/util';

export type UseSessionTranslationResult = {
  translated?: LogMessage[];
  isLoading: boolean;
  isError: boolean;
  status: 'pending' | 'success' | 'error';
  /** Imperative refresh — convenient when retrying after an error toast. */
  refetch: () => void;
};

/**
 * Translates the messages of a session into a target language via the
 * `translateLogs` REST endpoint. Cached by `(sessionId, language)` so flipping
 * the dropdown doesn't re-fetch the same combination twice.
 *
 * Stripped of side effects compared to legacy `useLogs`: no toast on completion,
 * no Redux dispatch. The owning component is responsible for surface-level UX.
 */
export const useSessionTranslation = (
  sessionId: string | undefined,
  language: string | undefined
): UseSessionTranslationResult => {
  const enabled = Boolean(sessionId) && Boolean(language) && isUuid(sessionId);

  const { data, isLoading, isError, status, refetch } = useQuery<LogMessage[]>({
    queryKey: ['translatedLogs', sessionId, language],
    queryFn: () => callPost(conversationsUtilsEndpoints.translateLogs(sessionId!, language!), {}),
    enabled,
  });

  return {
    translated: data,
    isLoading,
    isError,
    status,
    refetch: () => {
      void refetch();
    },
  };
};
